import { Controller } from "stimulus"

// Menu contextuel de ligne de tableau (bouton « ⋮ »).
// La liste est positionnee en `fixed` : le conteneur `.table-container` a
// `overflow-x: auto` et rognerait un menu en position absolue.
export default class extends Controller {
  static targets = ["trigger", "list"]

  connect() {
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    this.onScroll = this.onScroll.bind(this)
    this.onOtherOpen = this.onOtherOpen.bind(this)
    this.close = this.close.bind(this)
    document.addEventListener("rowmenu:open", this.onOtherOpen)
  }

  disconnect() {
    document.removeEventListener("rowmenu:open", this.onOtherOpen)
    this.unbindOpenListeners()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    if (!this.hasListTarget) return

    // Previent les autres menus ouverts de se fermer.
    document.dispatchEvent(new CustomEvent("rowmenu:open", { detail: { source: this.element } }))

    this.element.classList.add("is-open")
    this.setExpanded(true)
    this.position()
    this.bindOpenListeners()
  }

  close() {
    if (!this.isOpen()) return

    this.element.classList.remove("is-open")
    this.setExpanded(false)
    this.unbindOpenListeners()
  }

  isOpen() {
    return this.element.classList.contains("is-open")
  }

  setExpanded(open) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", open ? "true" : "false")
    }
  }

  // Place la liste sous le bouton, alignee a droite ; la retourne au-dessus
  // s'il n'y a pas assez de place en bas.
  position() {
    if (!this.hasListTarget || !this.hasTriggerTarget) return

    const list = this.listTarget
    const rect = this.triggerTarget.getBoundingClientRect()
    const gap = 4
    const margin = 8

    list.style.visibility = "hidden"
    const width = list.offsetWidth
    const height = list.offsetHeight

    let top = rect.bottom + gap
    if (top + height > window.innerHeight - margin && rect.top - gap - height >= margin) {
      top = rect.top - gap - height
    }

    let left = rect.right - width
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin))

    list.style.top = `${Math.round(top)}px`
    list.style.left = `${Math.round(left)}px`
    list.style.visibility = ""
  }

  bindOpenListeners() {
    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onDocumentKeydown)
    window.addEventListener("scroll", this.onScroll, true)
    window.addEventListener("resize", this.close)
    document.addEventListener("turbo:before-cache", this.close)
  }

  unbindOpenListeners() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onDocumentKeydown)
    window.removeEventListener("scroll", this.onScroll, true)
    window.removeEventListener("resize", this.close)
    document.removeEventListener("turbo:before-cache", this.close)
  }

  onDocumentClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  onDocumentKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      if (this.hasTriggerTarget) this.triggerTarget.focus()
    }
  }

  // Un defilement de la page (ou du tableau) ferme le menu, sauf celui de la liste.
  onScroll(event) {
    if (this.hasListTarget && this.listTarget.contains(event.target)) return
    this.close()
  }

  onOtherOpen(event) {
    if (event.detail && event.detail.source !== this.element) this.close()
  }
}
